import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PageContainer from '../../common/components/PageContainer';
import { useReadOnly } from '../../hooks/useReadOnly';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { MAX_RANGE_DAYS, apiErrorCode, isRangeTooLong, parseSettings, presetRange } from '../../utils/vinExtraction';
import { spacing } from '../../theming';
import VinExtractionHeader from '../../components/vinExtraction/VinExtractionHeader';
import StatsSection from '../../components/vinExtraction/StatsSection';
import RangeControls from '../../components/vinExtraction/RangeControls';
import BatchesSection from '../../components/vinExtraction/BatchesSection';
import { notifyVinError } from '../../components/vinExtraction/vinExtractionErrors';
import SettingsArea from './SettingsArea';
import VinExtractionNotAllowedPage from './VinExtractionNotAllowedPage';

const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const useRange = () => {
    const [range, setRange] = useState(() => ({ preset: '24h', ...presetRange('24h') }));

    const changeRange = ({ preset, from, to }) => {
        if (preset !== 'custom') return setRange({ preset, ...presetRange(preset) });
        // switching to Custom keeps the current window until both dates are picked
        if (!from || !to) return setRange(prev => ({ ...prev, preset }));
        // 90 local days across a DST change can be an hour over the API's limit: trim the start to 90 × 24 h, never send it
        setRange({ preset, from: isRangeTooLong(from, to) ? to.subtract(MAX_RANGE_DAYS * 24, 'hour') : from, to });
    };

    // presets are rolling windows: a refresh moves them to end now; a custom range stays as picked
    const rollForward = () =>
        setRange(prev => (prev.preset === 'custom' ? prev : { ...prev, ...presetRange(prev.preset) }));

    return { range, changeRange, rollForward };
};

/** GET /settings as an outcome: `{settings}` for a valid body, else `{status: invalid | notAllowed | error, error}`. */
const readSettings = async getSettings => {
    try {
        const settings = parseSettings(await getSettings());
        return settings ? { settings } : { status: 'invalid', error: null };
    } catch (error) {
        return { status: apiErrorCode(error) === 'NOT_ALLOWED' ? 'notAllowed' : 'error', error };
    }
};

/**
 * The settings on screen. A failed re-read keeps them (and says so); a failed first read, a bad body or a 403
 * NOT_ALLOWED replaces them. `loadSettings` resolves to the validated body, or null after showing why it could not.
 */
const useSettingsLoad = onFetched => {
    const { t } = useTranslation('vinExtraction');
    const { getSettings } = useVinExtractionService();
    const [load, setLoad] = useState({ status: 'loading', settings: null, error: null });

    const adoptSettings = settings => {
        setLoad({ status: 'ready', settings, error: null });
        onFetched();
    };

    const loadFailed = (status, error) => {
        if (load.settings && status === 'error') return notifyVinError(t, error);
        setLoad({ status, settings: null, error });
    };

    const loadSettings = async () => {
        const outcome = await readSettings(getSettings);
        if (outcome.settings) adoptSettings(outcome.settings);
        else loadFailed(outcome.status, outcome.error);
        return outcome.settings ?? null;
    };

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const retry = () => {
        setLoad({ status: 'loading', settings: null, error: null });
        loadSettings();
    };

    return { load, adoptSettings, loadSettings, retry };
};

/** Refresh: presets roll forward to now, the sections re-read (via the token) and so do the settings. */
const useRefresh = (rollForward, loadSettings) => {
    const [refreshToken, setRefreshToken] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        setRefreshing(true);
        rollForward();
        setRefreshToken(token => token + 1);
        await loadSettings();
        setRefreshing(false);
    };

    return { refreshToken, refreshing, refresh };
};

/** Super Admin › VIN extraction: status strip + settings edit, stats, then the batches with their cars and car drawer. */
export default function VinExtraction() {
    const readOnly = useReadOnly();
    const { range, changeRange, rollForward } = useRange();
    const [lastFetchedAt, setLastFetchedAt] = useState(null);
    const markFetched = () => setLastFetchedAt(dayjs());
    const settings = useSettingsLoad(markFetched);
    const { refreshToken, refreshing, refresh } = useRefresh(rollForward, settings.loadSettings);
    const sectionRange = useMemo(
        () => ({ from: range.from.toISOString(), to: range.to.toISOString(), tz: browserTz() }),
        [range]
    );

    if (settings.load.status === 'notAllowed') return <VinExtractionNotAllowedPage />;

    return (
        <PageContainer>
            <Flex vertical gap={spacing[3]}>
                <VinExtractionHeader
                    showControls
                    lastFetchedAt={lastFetchedAt}
                    refreshing={refreshing}
                    onRefresh={refresh}
                />
                <SettingsArea {...settings} readOnly={readOnly} />
                <StatsSection
                    range={sectionRange}
                    refreshToken={refreshToken}
                    onFetched={markFetched}
                    controls={<RangeControls range={range} onRangeChange={changeRange} />}
                    settings={settings.load.settings}
                />
                <BatchesSection
                    range={sectionRange}
                    refreshToken={refreshToken}
                    onFetched={markFetched}
                    settings={settings.load.settings}
                />
            </Flex>
        </PageContainer>
    );
}
